// MediaSelector.js — Fase 15.3: Modal selector de media reutilizable
// Uso: <MediaSelector onSelect={(media) => {}} onClose={() => {}} multiple={false} filterTipo="video" />
import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import {
  ADMIN_COLORS, AdminButton, AdminSmallButton, AdminModalOverlay, AdminModalContent,
  AdminModalTitle, AdminModalActions, AdminBadge, AdminLoading, AdminEmptyState,
  AdminSearchInput, AdminPagination, AdminInfoText
} from './AdminShared';
import { mediaLibraryService } from '../../services/api';
import { useProject } from '../../contexts/ProjectContext';

const FilterChip = styled.button`
  padding: 0.3rem 0.75rem; border-radius: 20px; font-size: 0.8rem; font-weight: 500;
  cursor: pointer; transition: all 0.2s; border: 1.5px solid;
  background: ${p => p.$active ? ADMIN_COLORS.accent : ADMIN_COLORS.white};
  color: ${p => p.$active ? '#fff' : ADMIN_COLORS.textLight};
  border-color: ${p => p.$active ? ADMIN_COLORS.accent : ADMIN_COLORS.border};
  &:hover { border-color: ${ADMIN_COLORS.accent}; }
`;

const Grid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 0.75rem; margin: 1rem 0; max-height: 400px; overflow-y: auto; padding: 4px;
`;

const Card = styled.div`
  background: ${ADMIN_COLORS.white}; border-radius: 10px; overflow: hidden; cursor: pointer;
  border: 2px solid ${p => p.$selected ? ADMIN_COLORS.accent : ADMIN_COLORS.border};
  box-shadow: 0 1px 4px rgba(0,0,0,0.06); transition: all 0.15s;
  &:hover { border-color: ${ADMIN_COLORS.accent}; }
`;

const Thumb = styled.div`
  height: 90px; background: ${ADMIN_COLORS.bg}; display: flex;
  align-items: center; justify-content: center; font-size: 2rem; position: relative;
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const Duration = styled.span`
  position: absolute; bottom: 4px; right: 4px; background: rgba(0,0,0,0.75);
  color: #fff; padding: 1px 5px; border-radius: 3px; font-size: 0.65rem;
`;

const Title = styled.div`
  padding: 0.5rem; font-size: 0.78rem; font-weight: 500; color: ${ADMIN_COLORS.text};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const TYPE_ICONS = { video: '🎬', pdf: '📑', imagen: '🖼️', audio: '🎵', presentacion: '📊', otro: '📄' };
const TYPE_LABELS = { video: 'Video', pdf: 'PDF', imagen: 'Imagen', audio: 'Audio' };

const formatDuration = (s) => { if (!s) return ''; return Math.floor(s/60) + ':' + String(s%60).padStart(2,'0'); };

const MediaSelector = ({ onSelect, onClose, multiple = false, filterTipo = '', title = 'Seleccionar Contenido' }) => {
  const { proyecto } = useProject();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [tipo, setTipo] = useState(filterTipo);
  const [selected, setSelected] = useState([]);
  const searchTimeout = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 24 };
      if (proyecto?.id) params.proyecto_id = proyecto.id;
      if (tipo) params.tipo = tipo;
      if (busqueda.trim()) params.busqueda = busqueda.trim();
      const res = await mediaLibraryService.list(params);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, tipo, busqueda, proyecto]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (val) => {
    setBusqueda(val); setPage(1);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(load, 400);
  };

  const toggleSelect = (item) => {
    if (multiple) {
      setSelected(prev => prev.find(s => s.id === item.id)
        ? prev.filter(s => s.id !== item.id) : [...prev, item]);
    } else {
      setSelected([item]);
    }
  };

  const handleConfirm = () => {
    if (selected.length === 0) return;
    onSelect(multiple ? selected : selected[0]);
  };

  return (
    <AdminModalOverlay onClick={onClose}>
      <AdminModalContent $extraWide onClick={e => e.stopPropagation()}>
        <AdminModalTitle>{title}</AdminModalTitle>

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.75rem' }}>
          <AdminSearchInput placeholder="Buscar..." value={busqueda}
            onChange={e => handleSearch(e.target.value)} style={{ width: 220 }} />
          {!filterTipo && (
            <>
              <FilterChip $active={!tipo} onClick={() => { setTipo(''); setPage(1); }}>Todos</FilterChip>
              {Object.keys(TYPE_LABELS).map(t => (
                <FilterChip key={t} $active={tipo === t} onClick={() => { setTipo(tipo === t ? '' : t); setPage(1); }}>
                  {TYPE_ICONS[t]} {TYPE_LABELS[t]}
                </FilterChip>
              ))}
            </>
          )}
          <AdminInfoText style={{ marginLeft: 'auto' }}>{total} disponibles</AdminInfoText>
        </div>

        {loading ? <AdminLoading>Cargando...</AdminLoading> : items.length === 0 ? (
          <AdminEmptyState>No se encontraron resultados</AdminEmptyState>
        ) : (
          <Grid>
            {items.map(item => (
              <Card key={item.id} $selected={selected.find(s => s.id === item.id)} onClick={() => toggleSelect(item)}>
                <Thumb>
                  {item.thumbnail_path ? <img src={item.thumbnail_path} alt="" /> : TYPE_ICONS[item.tipo]}
                  {item.duracion_segundos > 0 && <Duration>{formatDuration(item.duracion_segundos)}</Duration>}
                </Thumb>
                <Title title={item.titulo}>
                  <AdminBadge $bg={item.tipo === 'video' ? '#dbeafe' : '#fef3c7'}
                    $color={item.tipo === 'video' ? '#1d4ed8' : '#92400e'}
                    style={{ fontSize: '0.65rem', marginRight: 4, padding: '1px 5px' }}>
                    {TYPE_ICONS[item.tipo]}
                  </AdminBadge>
                  {item.titulo}
                </Title>
              </Card>
            ))}
          </Grid>
        )}

        {totalPages > 1 && (
          <AdminPagination>
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Ant</button>
            <span>{page}/{totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Sig</button>
          </AdminPagination>
        )}

        <AdminModalActions>
          <AdminButton $ghost onClick={onClose}>Cancelar</AdminButton>
          <AdminButton onClick={handleConfirm} disabled={selected.length === 0}>
            Seleccionar {selected.length > 0 ? `(${selected.length})` : ''}
          </AdminButton>
        </AdminModalActions>
      </AdminModalContent>
    </AdminModalOverlay>
  );
};

export default MediaSelector;
